import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../api/client.ts';

type SinceItem = {
  _t: 'sleep' | 'feed' | 'pump' | 'diaper';
  id: string;
  summary: string;
  created_by: string;
};
type SinceResponse = { cursor: number; events: SinceItem[] };

export function useEventPolling(
  babyId: string | null | undefined,
  meId: string | undefined,
  membersById: Record<string, { name: string; color: string | null } | undefined>,
  enabled = true,
) {
  const qc = useQueryClient();
  const cursorRef = useRef<number>(Math.floor(Date.now() / 1000));
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !babyId) return;
    let timer: number | null = null;
    let stopped = false;

    async function tick() {
      if (document.hidden) return;
      try {
        const r = await api<SinceResponse>(
          `/api/events/since?baby=${encodeURIComponent(babyId!)}&cursor=${cursorRef.current}`,
        );
        cursorRef.current = r.cursor;
        let invalidated = false;
        for (const ev of r.events) {
          const key = `${ev._t}:${ev.id}`;
          if (seenIds.current.has(key)) continue;
          seenIds.current.add(key);
          if (meId && ev.created_by !== meId) {
            const who = membersById[ev.created_by]?.name ?? 'Someone';
            toast(`${who} logged ${ev.summary}`, { duration: 4000 });
          }
          invalidated = true;
        }
        if (invalidated) {
          qc.invalidateQueries({ queryKey: ['sleep'] });
          qc.invalidateQueries({ queryKey: ['feed'] });
          qc.invalidateQueries({ queryKey: ['pump'] });
          qc.invalidateQueries({ queryKey: ['diaper'] });
        }
      } catch {
        // silent — next tick will retry
      }
    }

    function schedule() {
      if (stopped) return;
      timer = window.setTimeout(async () => {
        await tick();
        schedule();
      }, 10_000);
    }

    schedule();
    const onVis = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      if (timer != null) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [babyId, meId, enabled, qc, membersById]);
}
